import { Extension, Mark, mergeAttributes } from '@tiptap/core';

/*
 * Extensiones propias para que TipTap escriba clases (`ta-*`, `tc-*`, `hl-*`) en lugar de
 * `style`: la API limpia el HTML con una lista blanca que solo acepta esas clases
 * (`Manuals/HtmlCleaner.php` y su gemelo en `manuals.service.ts`).
 */

export const ALINEACIONES = ['left', 'center', 'right', 'justify'] as const;
export type Alineacion = (typeof ALINEACIONES)[number];

/** Colores de texto (`tc-*`) y de resaltado (`hl-*`) con su muestra para la barra. */
export const COLORES_TEXTO: Record<string, string> = {
  azul: '#005DB9',
  cian: '#02A9AD',
  verde: '#4E9A32',
  morado: '#720294',
  marino: '#013765',
  rojo: '#B51800',
  naranja: '#D66A1F',
  gris: '#666666',
};
export const RESALTADOS: Record<string, string> = {
  amarillo: '#FFF3A3',
  verde: '#D7F5C8',
  cian: '#C9F4F5',
  rosa: '#FAD3E4',
};

const claseCon = (prefijo: string, el: HTMLElement): string | null => {
  const c = [...el.classList].find((x) => x.startsWith(prefijo));
  return c ? c.slice(prefijo.length) : null;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    alineacion: { alinear: (a: Alineacion | null) => ReturnType };
    colorTexto: { ponerColor: (c: string) => ReturnType; quitarColor: () => ReturnType };
    resaltado: { resaltar: (c: string) => ReturnType; quitarResaltado: () => ReturnType };
  }
}

/** Alineación de párrafos y títulos como clase `ta-*`. */
export const Alineacion = Extension.create({
  name: 'alineacion',
  addGlobalAttributes() {
    return [
      {
        types: ['paragraph', 'heading'],
        attributes: {
          alinear: {
            default: null,
            parseHTML: (el) => claseCon('ta-', el),
            renderHTML: (a) => (a.alinear ? { class: `ta-${String(a.alinear)}` } : {}),
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      alinear:
        (a) =>
        ({ commands }) =>
          ['paragraph', 'heading']
            .map((t) => commands.updateAttributes(t, { alinear: a === 'left' ? null : a }))
            .some(Boolean),
    };
  },
});

/** Color de texto: `<span class="tc-azul">`. */
export const ColorTexto = Mark.create({
  name: 'colorTexto',
  addAttributes() {
    return {
      color: {
        default: null,
        parseHTML: (el) => claseCon('tc-', el),
        renderHTML: (a) => ({ class: `tc-${String(a.color)}` }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span', getAttrs: (el) => (claseCon('tc-', el) ? null : false) }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      ponerColor:
        (color) =>
        ({ commands }) =>
          commands.setMark(this.name, { color }),
      quitarColor:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/** Resaltado: `<mark class="hl-amarillo">`. */
export const Resaltado = Mark.create({
  name: 'resaltado',
  addAttributes() {
    return {
      color: {
        default: 'amarillo',
        parseHTML: (el) => claseCon('hl-', el) ?? 'amarillo',
        renderHTML: (a) => ({ class: `hl-${String(a.color)}` }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'mark' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['mark', mergeAttributes(HTMLAttributes), 0];
  },
  addCommands() {
    return {
      resaltar:
        (color) =>
        ({ commands }) =>
          commands.setMark(this.name, { color }),
      quitarResaltado:
        () =>
        ({ commands }) =>
          commands.unsetMark(this.name),
    };
  },
});

/**
 * Las imágenes se guardan con ruta relativa (`src/content/manual-enarm-plus/…`) y la plataforma
 * las firma al servirlas; en el editor se muestran con la URL del CDN.
 */
export function aEditor(html: string, cdnBase: string | null): string {
  if (!cdnBase) return html;
  return html.replace(/(<img\b[^>]*?\ssrc=")(src\/content\/)/gi, `$1${cdnBase}/$2`);
}

export function desdeEditor(html: string, cdnBase: string | null): string {
  if (!cdnBase) return html;
  const base = cdnBase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return html.replace(new RegExp(`(<img\\b[^>]*?\\ssrc=")${base}/(src/content/)`, 'gi'), '$1$2');
}
